// src/components/ChangePassword.js
import React, { useState } from "react";
import "../styles/ChangePassword.css";
import config from "../config";

export default function ChangePassword({ onClose, sellerId }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }

    if (currentPassword === newPassword) {
      setError("New password must be different from current password");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${config.baseUrl}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          login: localStorage.getItem("username"),
          currentPassword: currentPassword,
          newPassword: newPassword,
        }),
      });

      if (response.ok) {
        setSuccess("✅ Password changed successfully!");
        // Reset form
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        // Close after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(
          errorData.message || "Failed to change password. Please try again."
        );
      }
    } catch (err) {
      console.error("Error changing password:", err);
      setError("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = (pwd) => {
    if (!pwd) return { strength: 0, label: "" };
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[!@#$%^&*]/.test(pwd)) strength++;

    const labels = ["", "Weak", "Fair", "Good", "Strong"];
    const colors = ["", "#ef4444", "#f59e0b", "#3b82f6", "#16a34a"];
    return {
      strength,
      label: labels[strength],
      color: colors[strength],
    };
  };

  const newPasswordStrength = passwordStrength(newPassword);

  return (
    <div className="password-modal-overlay">
      <div className="password-modal">
        <button className="modal-close" onClick={onClose}>
          ✕
        </button>

        <div className="modal-header">
          <h2>🔐 Change Your Password</h2>
          <p>Secure your account with a new password</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleChangePassword} className="password-form">
          {/* Current Password */}
          <div className="form-group">
            <label>Current Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPasswords(!showPasswords)}
              >
                {showPasswords ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className="form-group">
            <label>New Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPasswords(!showPasswords)}
              >
                {showPasswords ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {newPassword && (
              <div className="password-strength">
                <div className="strength-bars">
                  {[1, 2, 3, 4].map((bar) => (
                    <div
                      key={bar}
                      className={`strength-bar ${
                        bar <= newPasswordStrength.strength ? "filled" : ""
                      }`}
                      style={{
                        backgroundColor:
                          bar <= newPasswordStrength.strength
                            ? newPasswordStrength.color
                            : "#e5e7eb",
                      }}
                    />
                  ))}
                </div>
                <span
                  className="strength-label"
                  style={{ color: newPasswordStrength.color }}
                >
                  {newPasswordStrength.label}
                </span>
              </div>
            )}

            {/* Password Requirements */}
            <div className="password-requirements">
              <p className="requirements-title">Password must contain:</p>
              <ul>
                <li className={newPassword.length >= 8 ? "met" : ""}>
                  {newPassword.length >= 8 ? "✓" : "○"} At least 8 characters
                </li>
                <li
                  className={
                    /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)
                      ? "met"
                      : ""
                  }
                >
                  {/[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)
                    ? "✓"
                    : "○"}{" "}
                  Uppercase and lowercase letters
                </li>
                <li className={/\d/.test(newPassword) ? "met" : ""}>
                  {/\d/.test(newPassword) ? "✓" : "○"} At least one number
                </li>
                <li className={/[!@#$%^&*]/.test(newPassword) ? "met" : ""}>
                  {/[!@#$%^&*]/.test(newPassword) ? "✓" : "○"} At least one
                  special character (!@#$%^&*)
                </li>
              </ul>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label>Confirm New Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                required
                disabled={loading}
              />
              {confirmPassword && newPassword === confirmPassword && (
                <span className="match-indicator">✓</span>
              )}
              {confirmPassword && newPassword !== confirmPassword && (
                <span className="mismatch-indicator">✕</span>
              )}
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="error-text">Passwords do not match</p>
            )}
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            >
              {loading ? "⏳ Changing..." : "✓ Change Password"}
            </button>
          </div>
        </form>

        <div className="modal-tips">
          <p>💡 Tip: Use a mix of uppercase, lowercase, numbers, and symbols for a strong password</p>
        </div>
      </div>
    </div>
  );
}
